import {z} from 'zod';

// Enums

export const WhatsAppConnectionConfigurationStateSchema = z.enum([
  'not_configured',
  'disabled',
  'configured',
]);

export const WhatsAppConnectionVerificationStateSchema = z.enum(['not_verified', 'verified']);

export const WhatsAppSignupScenarioSchema = z.enum(['standard', 'coexistence']);

export const WhatsAppSignupStateSchema = z.enum([
  'started',
  'expired',
  'cancelled',
  'failed',
  'completed',
]);

export const WhatsAppSignupFailureReasonSchema = z.enum([
  'code_exchange_failed',
  'account_not_granted',
  'phone_number_not_in_account',
  'phone_number_not_selected',
  'account_connected_elsewhere',
  'another_account_connected',
  'webhook_subscription_failed',
  'phone_number_registration_failed',
  'meta_request_failed',
]);

// Request/Response Body Schemas

export const WhatsAppConnectionVerificationResponseBodySchema = z.object({
  state: WhatsAppConnectionVerificationStateSchema,
  verifiedAt: z.string().nullable(),
});

export const WhatsAppConnectionResponseBodySchema = z.object({
  configurationState: WhatsAppConnectionConfigurationStateSchema,
  phoneNumberId: z.string().nullable(),
  wabaId: z.string().nullable(),
  configuredAt: z.string().nullable(),
  sendingVerification: WhatsAppConnectionVerificationResponseBodySchema,
  receivingVerification: WhatsAppConnectionVerificationResponseBodySchema,
});

export const WhatsAppSignupResponseBodySchema = z.object({
  id: z.string(),
  scenario: WhatsAppSignupScenarioSchema,
  state: WhatsAppSignupStateSchema,
  failureReason: WhatsAppSignupFailureReasonSchema.nullable(),
  expiresAt: z.string(),
  appId: z.string(),
  configurationId: z.string(),
});

export const StartWhatsAppSignupRequestBodySchema = z.object({
  scenario: WhatsAppSignupScenarioSchema,
});

export const CompleteWhatsAppSignupRequestBodySchema = z.object({
  code: z.string(),
  wabaId: z.string(),
  phoneNumberId: z.string().nullable(),
});

export const CancelWhatsAppSignupRequestBodySchema = z.object({
  currentStep: z.string().nullable(),
  errorCode: z.string().nullable(),
});

// API Response Schemas

export const GetWhatsAppConnectionApiResponseSchema = WhatsAppConnectionResponseBodySchema; // status 200
export const StartWhatsAppSignupApiResponseSchema = WhatsAppSignupResponseBodySchema; // status 201
export const CompleteWhatsAppSignupApiResponseSchema = WhatsAppSignupResponseBodySchema; // status 200
export const CancelWhatsAppSignupApiResponseSchema = WhatsAppSignupResponseBodySchema; // status 200

// API Arguments Schemas

export const GetWhatsAppConnectionApiArgSchema = z.void();

export const StartWhatsAppSignupApiArgSchema = StartWhatsAppSignupRequestBodySchema;

export const CompleteWhatsAppSignupApiArgSchema = z
  .object({signupId: z.string()})
  .merge(CompleteWhatsAppSignupRequestBodySchema);

export const CancelWhatsAppSignupApiArgSchema = z
  .object({signupId: z.string()})
  .merge(CancelWhatsAppSignupRequestBodySchema);

// Exported Types (Inferred)

export type WhatsAppConnectionConfigurationState = z.infer<
  typeof WhatsAppConnectionConfigurationStateSchema
>;
export type WhatsAppConnectionVerificationState = z.infer<
  typeof WhatsAppConnectionVerificationStateSchema
>;
export type WhatsAppSignupScenario = z.infer<typeof WhatsAppSignupScenarioSchema>;
export type WhatsAppSignupState = z.infer<typeof WhatsAppSignupStateSchema>;
export type WhatsAppSignupFailureReason = z.infer<typeof WhatsAppSignupFailureReasonSchema>;
export type WhatsAppConnectionVerificationResponseBody = z.infer<
  typeof WhatsAppConnectionVerificationResponseBodySchema
>;
export type WhatsAppConnectionResponseBody = z.infer<typeof WhatsAppConnectionResponseBodySchema>;
export type WhatsAppSignupResponseBody = z.infer<typeof WhatsAppSignupResponseBodySchema>;

// API Response Types

export type GetWhatsAppConnectionApiResponse = z.infer<
  typeof GetWhatsAppConnectionApiResponseSchema
>;
export type StartWhatsAppSignupApiResponse = z.infer<typeof StartWhatsAppSignupApiResponseSchema>;
export type CompleteWhatsAppSignupApiResponse = z.infer<
  typeof CompleteWhatsAppSignupApiResponseSchema
>;
export type CancelWhatsAppSignupApiResponse = z.infer<typeof CancelWhatsAppSignupApiResponseSchema>;

// API Argument Types

export type GetWhatsAppConnectionApiArg = z.infer<typeof GetWhatsAppConnectionApiArgSchema>;
export type StartWhatsAppSignupApiArg = z.infer<typeof StartWhatsAppSignupApiArgSchema>;
export type CompleteWhatsAppSignupApiArg = z.infer<typeof CompleteWhatsAppSignupApiArgSchema>;
export type CancelWhatsAppSignupApiArg = z.infer<typeof CancelWhatsAppSignupApiArgSchema>;
